import { useState } from 'react';
import { NavBar } from '../../components/navBar.tsx';
import { PageTitle } from '../../components/pageTitle.tsx';
import { useFullScreenToggle } from '../../hooks/useFullscreenToggle.ts';
import { ManualTask } from '../../types/sharedTypeImpl.ts';
import AutoTasksTable from './auto-tasks/autoTasksTable.tsx';
import { CloseTaskDialog, CloseTaskMode } from './manual-tasks/closeTaskDialog.tsx';
import { ManualTaskDialog } from './manual-tasks/manualTaskDialog.tsx';
import { ManualTasksTableCompact } from './manual-tasks/manualTasksTableCompact.tsx';
import { NoticeDialog } from './noticeDialog.tsx';
import { NoticePanel } from './noticePanel.tsx';
import { ShiftMetricsPanel } from './shiftMetricsPanel.tsx';
import { PageIssuesTable } from './pageIssuesTable.tsx';
import { ScheduledTasksDialog } from './manual-tasks/scheduledTasksDialogs.tsx';
import { TasksLogDialog } from './manual-tasks/tasksLogDialog.tsx';
import { useFetchData } from './useFetchData.ts';
import { WorstEquipmentsTable } from './worstEquipmentsTable.tsx';

interface ManualTaskDialogState {
    isOpen: boolean;
    task?: ManualTask;
}

interface CloseTaskDialogState {
    task: ManualTask;
    mode: CloseTaskMode;
}

export function TasksPage() {
    const [isNoticeDialogOpen, setIsNoticeDialogOpen] = useState(false);
    const [manualTaskDialog, setManualTaskDialog] = useState<ManualTaskDialogState | null>(null);
    const [isTaskLogOpen, setIsTaskLogOpen] = useState(false);
    const [isScheduledTasksOpen, setIsScheduledTasksOpen] = useState(false);
    const [closeTaskDialog, setCloseTaskDialog] = useState<CloseTaskDialogState | null>(null);

    const pauseFetching = isNoticeDialogOpen || manualTaskDialog?.isOpen || isTaskLogOpen || !!closeTaskDialog;
    const {
        autoTasks,
        autoTasksError,
        manualTasksCurrent,
        manualTasksScheduled,
        manualTasksError,
        refetchManualTasks,
        notice,
        refetchNotice,
        worstEquipments,
        pageIssues,
    } = useFetchData({ pauseFetching });

    const { isFullscreen } = useFullScreenToggle();

    const error = autoTasksError ?? manualTasksError;
    return (
        <>
            <PageTitle title='Tasks' />
            {!isFullscreen && <NavBar />}
            <div className='content' style={{ margin: '0px 40px 32px 40px' }}>
                <div className='flex--horz'>{error && <p className='page-error'>{error?.message}</p>}</div>

                <div style={{ display: 'flex', flexDirection: 'row', gap: '60px', alignItems: 'flex-start', marginTop: '24px' }}>
                    <AutoTasksTable style={{ flex: '0 1 60%' }} tasks={autoTasks ?? []} />

                    <div style={{ flex: '0 1 40%', display: 'flex', flexDirection: 'column', gap: '16px', height: '100vh' }}>
                        <ShiftMetricsPanel />
                        <NoticePanel style={{ flex: '0 0 auto' }} notice={notice} onNoticeClicked={() => setIsNoticeDialogOpen(true)} />

                        <PageIssuesTable pageIssues={pageIssues} />

                        <div>
                            <ManualTasksTableCompact
                                style={{ minHeight: '100px' }}
                                tasks={manualTasksCurrent ?? []}
                                useLargeFont={true}
                                headerActions={
                                    <div className='button-bar'>
                                        <button onClick={() => setIsScheduledTasksOpen(true)} children='Scheduled tasks' />
                                        <button onClick={() => setIsTaskLogOpen(true)} children='Tasks log' />
                                        <button onClick={() => setManualTaskDialog({ isOpen: true })} children='Create new task' />
                                    </div>
                                }
                                onEditTaskClick={(task) => setManualTaskDialog({ isOpen: true, task })}
                                onCancelTaskClick={(task) => setCloseTaskDialog({ task, mode: 'cancel' })}
                                onCompleteTaskClick={(task) => setCloseTaskDialog({ task, mode: 'complete' })}
                            />
                        </div>

                        <WorstEquipmentsTable worstEquipments={worstEquipments ?? []} />
                    </div>
                </div>
            </div>

            {isNoticeDialogOpen && <NoticeDialog initialNotice={notice} onSuccess={refetchNotice} onClose={() => setIsNoticeDialogOpen(false)} />}
            {isTaskLogOpen && <TasksLogDialog onClose={() => setIsTaskLogOpen(false)} />}
            {isScheduledTasksOpen && (
                <ScheduledTasksDialog
                    tasks={manualTasksScheduled ?? []}
                    onEditTaskClick={(task) => setManualTaskDialog({ isOpen: true, task })}
                    onCancelTaskClick={(task) => setCloseTaskDialog({ task, mode: 'cancel' })}
                    onCompleteTaskClick={(task) => setCloseTaskDialog({ task, mode: 'complete' })}
                    onClose={() => setIsScheduledTasksOpen(false)}
                />
            )}
            {manualTaskDialog?.isOpen && (
                <ManualTaskDialog task={manualTaskDialog.task} onSuccess={refetchManualTasks} onClose={() => setManualTaskDialog(null)} />
            )}
            {closeTaskDialog && (
                <CloseTaskDialog
                    task={closeTaskDialog.task}
                    mode={closeTaskDialog.mode}
                    onSuccess={() => refetchManualTasks()}
                    onClose={() => setCloseTaskDialog(null)}
                />
            )}
        </>
    );
}
